import { AlertTriangle, Download, Loader2, XCircle } from "lucide-react";
import type { MCAPSuspensionPayload } from "../../hooks/useCopilotRuntime";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

interface McapApprovalRequestViewProps {
  payload: MCAPSuspensionPayload;
  submitting: boolean;
  errorMsg: string | null;
  onApprove: () => void;
}

/** HITL approval prompt shown while the workflow is suspended. */
export function McapApprovalRequestView({
  payload,
  submitting,
  errorMsg,
  onApprove,
}: McapApprovalRequestViewProps) {
  return (
    <div className="my-3 rounded-lg border border-amber-600/40 bg-amber-950/30 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-amber-600/30 bg-amber-900/20 px-4 py-2.5">
        <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-wider text-amber-300">
          Edge Action Required — MCAP Log Extraction
        </span>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-3">
        {/* Justification */}
        <div>
          <p className="text-[11px] uppercase tracking-wide text-neutral-500 mb-1">
            Justification
          </p>
          <p className="text-sm text-neutral-200 leading-relaxed">
            {payload.justification}
          </p>
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <div>
            <p className="text-neutral-500 mb-0.5">Device</p>
            <p className="text-neutral-300 truncate">
              {payload.device_name || "Selected device"}
            </p>
          </div>
          <div>
            <p className="text-neutral-500 mb-0.5">Incident</p>
            <p className="text-neutral-300 truncate">
              {payload.incident_reference || "Selected incident"}
            </p>
          </div>
          {payload.time_window_seconds !== undefined && (
            <div>
              <p className="text-neutral-500 mb-0.5">Time Window</p>
              <p className="text-neutral-300">{payload.time_window_seconds}s</p>
            </div>
          )}
          {payload.pipelines && payload.pipelines.length > 0 && (
            <div className="col-span-2">
              <p className="text-neutral-500 mb-0.5">Pipelines</p>
              <div className="flex flex-wrap gap-1">
                {payload.pipelines.map((p) => (
                  <span
                    key={p}
                    className="rounded bg-neutral-700/60 px-1.5 py-0.5 font-mono text-[10px] text-neutral-300"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Projected file size */}
        <div className="flex items-center gap-2 rounded-md bg-neutral-800/50 border border-neutral-700/40 px-3 py-2">
          <Download className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
          <span className="text-xs text-neutral-400">
            Projected transfer over cellular:
          </span>
          <span className="ml-auto text-xs font-semibold text-neutral-200">
            ~{formatBytes(payload.estimated_file_size_bytes)}
          </span>
        </div>

        {/* Action area */}
        {errorMsg && (
          <div className="flex items-center gap-2 rounded-md border border-red-600/40 bg-red-950/30 px-4 py-2">
            <XCircle className="h-4 w-4 text-red-400 shrink-0" />
            <span className="text-sm text-red-300">{errorMsg}</span>
          </div>
        )}
        <button
          onClick={onApprove}
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-500 active:bg-amber-700 disabled:opacity-60"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitting ? "Dispatching…" : "Confirm Log Extraction"}
        </button>
      </div>
    </div>
  );
}
